import { Box } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import type { ReactNode } from 'react';

export const SORTABLE_HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  cursor: 'pointer',
  userSelect: 'none',
  whiteSpace: 'nowrap',
  flexWrap: 'nowrap',
  '&:hover': { opacity: 0.7 },
} as const;

/** Keep sortable column headers on one line (apply to `TableCell` in `TableHead`). */
export const TABLE_HEAD_NOWRAP_SX = { whiteSpace: 'nowrap' } as const;

/** Keep action icon buttons on one row (apply to `TableCell` in `TableBody`). */
export const TABLE_ACTIONS_CELL_SX = { whiteSpace: 'nowrap' } as const;

/** Body cells where long text truncates with ellipsis — set `title` on the cell for the full value. Pair with a column `width` on the header cell when using `tableLayout: 'fixed'`. */
export const TABLE_BODY_ELLIPSIS_CELL_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

/** Body cells for formatted dates/times — do not wrap. */
export const TABLE_DATE_CELL_SX = { whiteSpace: 'nowrap' } as const;

export function renderSortIcon(active: boolean, direction: 'asc' | 'desc') {
  if (!active) {
    return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
  }
  return direction === 'asc'
    ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
    : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
}

export interface SortableTableHeaderProps {
  label: ReactNode;
  active: boolean;
  direction: 'asc' | 'desc';
  onClick: () => void;
  alignRight?: boolean;
}

export function SortableTableHeader({
  label,
  active,
  direction,
  onClick,
  alignRight = false,
}: SortableTableHeaderProps) {
  return (
    <Box
      sx={{ ...SORTABLE_HEADER_SX, justifyContent: alignRight ? 'flex-end' : 'flex-start' }}
      onClick={onClick}
    >
      {typeof label === 'string' ? <strong>{label}</strong> : label}
      {renderSortIcon(active, direction)}
    </Box>
  );
}
